  import { getElementText, getElementPlaceholder, getElementCssSelector, getContainingIFrame, cleanHTML, generateXPath, generateCssPath, robustQuerySelector, highlighterLogger, getAllDocumentElementsIncludingShadow } from './utils';
  import { isClickableElement, isFillableElement, getParentNode, getElementDepth, doubleQuoteString } from '@probolabs/probo-shared';
// maximum number of characters in a logical name
const MAX_LOGICAL_NAME_LENGTH = 40;
// minimum number of characters in text content to be considered a text element
const MIN_TEXT_LENGTH = 3;
/**
  * Query elements by CSS selector with :has-text("...") pseudo-selector
  * @param {string} selector - CSS selector with optional :has-text("...") pseudo-selector
  * @param {Element|Document} context - Context element to query within (defaults to document)
  * @returns {Element[]} Array of matched elements
  * @example
  * // Find all buttons with text "Submit"
  * querySelectorAllByText('button:has-text("Submit")')
  * 
  * // Find element with specific role and text
  * querySelectorAllByText('[role="button"]:has-text("Click me")')
  * 
  * // Complex selector with descendants
  * querySelectorAllByText('button:has-text("foo") > div > ul')
  * 
  * // Multiple components
  * querySelectorAllByText('.container > button:has-text("Submit")')
  * 
  * // Multiple text filters
  * querySelectorAllByText('button:has-text("foo") > div:has-text("bar") > span')
  */
  function querySelectorAllByText(selector, context = document) {
	// Local helper function to compare two strings by normalizing whitespace and ignoring case
	// mimic the behavior of Playwright's :has-text() selector modifier
	function pwHasText(str1, str2) {
		if (!str1 || !str2) return false;
		
		// Normalize whitespace by replacing multiple spaces with single space and trim
		const normalize = (str) => cleanText(str.toLowerCase());
		
		const normalized1 = normalize(str1);
		const normalized2 = normalize(str2);
		
		return normalized1.includes(normalized2);
	}
	
	// Check if selector contains :has-text() pseudo-selector
	if (!selector.includes(':has-text(')) {
		// No :has-text pseudo-selector, use regular querySelectorAll
		return Array.from(robustQuerySelector(selector, context, true));
	}
	
	// Split selector by combinators while preserving them
	// Matches: >, +, ~, or space (descendant combinator)
	const parts = [];
	let currentPart = '';
	let inBracket = false;
	let inTextPseudo = false;
	let inTextQuotes = false;
	let i = 0;
	
	while (i < selector.length) {
		const char = selector[i];
		
		// Track :has-text("...") to avoid splitting inside it
		if (selector.substring(i, i + 10) === ':has-text(') {
			inTextPseudo = true;
			inTextQuotes = false;
		}
		if (inTextPseudo && char === '"' && !inTextQuotes) {
			inTextQuotes = true;
		} else if (inTextPseudo && char === '"' && inTextQuotes) {
			// Check if this quote is escaped
			if (i > 0 && selector[i - 1] === '\\') {
				// This is an escaped quote, don't toggle inTextQuotes
			} else {
				inTextQuotes = false;
			}
		}
		if (inTextPseudo && char === ')' && !inTextQuotes) {
			inTextPseudo = false;
			currentPart += char;
			i++;
			continue;
		}
		
		// Track brackets to avoid splitting on combinators inside attribute selectors
		if (char === '[') inBracket = true;
		if (char === ']') inBracket = false;
		
		// Check for combinators (not inside brackets or :has-text())
		if (!inBracket && !inTextPseudo && (char === '>' || char === '+' || char === '~')) {
			if (currentPart.trim()) parts.push({ selector: currentPart.trim(), combinator: null });
			parts.push({ selector: null, combinator: char.trim() });
			currentPart = '';
			i++;
			continue;
		}
		
		// Check for space combinator (descendant)
		if (!inBracket && !inTextPseudo && char === ' ') {
			// Skip multiple spaces
			while (i < selector.length && selector[i] === ' ') i++;
			// Check if next char is a combinator (>, +, ~)
			if (i < selector.length && !'><+~'.includes(selector[i])) {
				if (currentPart.trim()) {
					parts.push({ selector: currentPart.trim(), combinator: null });
					parts.push({ selector: null, combinator: ' ' });
					currentPart = '';
				}
			}
			continue;
		}
		
		currentPart += char;
		i++;
	}
	if (currentPart.trim()) parts.push({ selector: currentPart.trim(), combinator: null });
	
	// Filter out combinator-only entries and rebuild as selector+combinator pairs
	const selectorParts = [];
	for (let j = 0; j < parts.length; j++) {
		if (parts[j].selector) {
			const combinator = (j + 1 < parts.length && parts[j + 1].combinator) ? parts[j + 1].combinator : null;
			// Extract text value if present
			const textMatch = parts[j].selector.match(/:has-text\("((?:[^"\\]|\\.)*)"\)/);
			const textValue = textMatch ? textMatch[1] : null;
			const selectorWithoutText = textMatch ? parts[j].selector.replace(/:has-text\("((?:[^"\\]|\\.)*)"\)/, '') : parts[j].selector;
			
			selectorParts.push({ 
				selector: parts[j].selector,
				selectorWithoutText,
				textValue,
				combinator 
			});
		}
	}
	
	// Process selector parts sequentially, applying text filters as we go
	let currentElements = [context];
	
	for (let partIndex = 0; partIndex < selectorParts.length; partIndex++) {
		const part = selectorParts[partIndex];
		const nextElements = [];
		
		for (const ctx of currentElements) {
			let candidates;
			
			if (partIndex === 0 && ctx === context) {
				// First part, query from document/context
				const querySelector = part.selectorWithoutText.trim() || '*';
				candidates = Array.from(robustQuerySelector(querySelector, ctx, true));
				highlighterLogger.debug(`querySelectorAllByText: first part, querySelector=${querySelector}, candidates=`, candidates);
			} 
			else {
				// Subsequent parts, use combinator
				const prevPart = selectorParts[partIndex - 1];
				const combinator = prevPart.combinator;
				const querySelector = part.selectorWithoutText.trim() || '*';
				
				if (combinator === '>') {
					// Direct children
					candidates = Array.from(ctx.children).filter(child => {
						return child.matches(querySelector);
					});
				} else if (combinator === '+') {
					// Next sibling
					const sibling = ctx.nextElementSibling;
					candidates = (sibling && sibling.matches(querySelector)) ? [sibling] : [];
				} else if (combinator === '~') {
					// Following siblings
					candidates = [];
					let sibling = ctx.nextElementSibling;
					while (sibling) {
						if (sibling.matches(querySelector)) {
							candidates.push(sibling);
						}
						sibling = sibling.nextElementSibling;
					}
				} 
				else {
					// Descendant (space)
					candidates = Array.from(robustQuerySelector(querySelector, ctx, true));
				}
			}
			
			// Apply text filter if present
			if (part.textValue !== null) {
				candidates = candidates.filter(el => pwHasText(getElementText(el), part.textValue));
			}
			
			nextElements.push(...candidates);
		}
		
		currentElements = nextElements;
		
		// If no matches, bail early
		if (currentElements.length === 0) {
			return [];
		}
	}
	
	return currentElements;
}

const cleanText = (text) => {
	return text.replace(/\s+/g, ' ').trim();
}

// Helper function to get name properties (logicalName and selector)
// Priority: text (if clickable and not input) -> name -> title -> aria-label
const getNameProps = (element) => {
	const isClickable = isClickableElement(element);
	const text = cleanText(getElementText(element));
	const name = element.getAttribute('name');
	const title = element.getAttribute('title');
	const ariaLabel = element.getAttribute('aria-label');
	const placeholder = element.getAttribute('placeholder');
	const role = element.getAttribute('role');
	
	let logicalName = '';
	let selector = '';
	let useText = false;
	
	// if the role starts with 'row', return empty logicalName and selector
	if (role?.startsWith('row')) {
		return {
			logicalName: '',
			selector: '',
			useText: false
		};
	}
	// Priority: text (if clickable and not input) -> name -> title -> aria-label
	if (text && text.length >= MIN_TEXT_LENGTH && text.length <= MAX_LOGICAL_NAME_LENGTH && isClickable && !isFillableElement(element)) {
		logicalName = text;
		selector = `:has-text(${doubleQuoteString(text)})`;
		useText = true;
	} else if (name && name.length <= MAX_LOGICAL_NAME_LENGTH) {
		logicalName = name;
		selector = `[name=${doubleQuoteString(name)}]`;
	} else if (title && title.length <= MAX_LOGICAL_NAME_LENGTH) {
		logicalName = title;
		selector = `[title=${doubleQuoteString(title)}]`;
	} else if (ariaLabel && ariaLabel.length <= MAX_LOGICAL_NAME_LENGTH) {
		logicalName = ariaLabel;
		selector = `[aria-label=${doubleQuoteString(ariaLabel)}]`;
	} else if (placeholder && placeholder.length <= MAX_LOGICAL_NAME_LENGTH) {
		logicalName = placeholder;
		selector = `[placeholder=${doubleQuoteString(placeholder)}]`;
	}
	
	return {
		logicalName,
		selector,
		useText
	};
};


/**
 * Individual strategy functions
 */

function tryRoleStrategy(element) {    
	const role = element.getAttribute('role');
	const tag = element.tagName.toLowerCase();
	const { logicalName, selector } = getNameProps(element);
	if (logicalName) {
		// Role-based strategy
		if (role) {
			return { 
				strategy: 'role', 
				selector: `[role=${doubleQuoteString(role)}]${selector}`
			};
		}
	
		// Tag-based role (button, a, input, etc.) + name
		if (['button', 'a', 'input', 'textarea', 'select'].includes(tag)) {
			return { 
				strategy: 'role', 
				selector: `${tag}${selector}`
			};
		}
	}
	
	return null;
}

function tryPlaceholderStrategy(element) {
	const placeholder = element.getAttribute('placeholder');
	if (placeholder) {
		return { 
			strategy: 'placeholder', 
			selector: `[placeholder=${doubleQuoteString(placeholder)}]` 
		};
	}
	return null;
}

function tryLabelStrategy(element) {
	const tag = element.tagName.toLowerCase();
	if (['input', 'textarea', 'select'].includes(tag)) {
		const id = element.id;
		if (id) {
			const labels = robustQuerySelector(`label[for=${doubleQuoteString(id)}]`, document, true);
			if (labels.length === 1) { //make sure id is unique
				const labelText = cleanText(getElementText(labels[0]));
				if (labelText) {
					const labelSelector = `label:has-text(${doubleQuoteString(labelText)})`;
					try {
						const els = querySelectorAllByText(labelSelector, document);
						if (els.length === 1) {
							return { 
								strategy: 'label for',
								label_selector: labelSelector
							};
						}
					} catch (error) {
						highlighterLogger.error(`internal error: while querying label selector ${labelSelector} for element ${element.outerHTML}`, error);
						throw error;
					}
				}
			}
		}
	}
	return null;
}

function tryAriaLabelledByStrategy(element) {
	const arialabeledBy = element.getAttribute('aria-labelledby');
	const tag = element.tagName.toLowerCase();
	
	if (arialabeledBy) {
		const labels = robustQuerySelector(`#${CSS.escape(arialabeledBy)}`, document, true);
		if (labels.length === 1) {
			const labelText = cleanText(getElementText(labels[0]));
			if (labelText) { //verify the label text is unique
				const labelSelector = `${tag}:has-text(${doubleQuoteString(labelText)})`;
				try {
					const els = querySelectorAllByText(labelSelector, document);
					if (els.length === 1) {
						return { 
							strategy: 'label by',   
							label_selector: labelSelector
						};
					}
				} catch (error) {
					highlighterLogger.error(`internal error: while querying aria-labelledby selector ${labelSelector} for element ${element.outerHTML}`, error);
					throw error;
				}
			}
		}
	}
	return null;
}

function tryNameStrategy(element) {	
	let { logicalName, selector, useText } = getNameProps(element);

	//if text wrap the selector in brackets since there is no other attribute used
	if (useText) {
		const tag = element.tagName.toLowerCase();
		selector = `${tag}${selector}`;
	}
	
	if (logicalName) {
		return { 
			strategy: 'name', 
			selector: selector
		};
	}
	return null;x
}

function tryDataTestIdStrategy(element) {
	const dataTestId = element.getAttribute('data-testid');
	if (dataTestId) { //check if the data-testid is unique
		const matchedElements = robustQuerySelector(`[data-testid=${doubleQuoteString(dataTestId)}]`, document, true);
		if (matchedElements.length === 1) {
			return { 
				strategy: 'data-testid',			
				selector: `[data-testid=${doubleQuoteString(dataTestId)}]` 
			};
		}
	}
	return null;
}

function tryIdStrategy(element) {
	const id = element.getAttribute('id');
	if (id && !/:|\s|\\/.test(id)) { //avoid using id if it contain unusual characters (react creates ids like this: ":r:42")
		//check if the id is unique by querying the id selector
		const matchedElements = robustQuerySelector(`#${CSS.escape(id)}`, document, true);	
		if (matchedElements.length === 1) {
			return { 
				strategy: 'id', 
				selector: `#${CSS.escape(id)}` 
			};
		}
	}
	return null;
}

//Currently unused but keeping it here for future reference
function tryClassStrategy(element) {
	const classes = Array.from(element.classList).map(cls => CSS.escape(cls));
	if (classes.length > 0) {
		return { 
			strategy: 'class', 
			selector: `.${classes.join('.')}` 
		};
	}
	return null;
}

// Try all strategies on an element in order of precedence
function attemptStrategiesOnElement(element) {
	const strategies = [
		tryRoleStrategy,      		
		tryAriaLabelledByStrategy,
		tryLabelStrategy,
		tryDataTestIdStrategy,
		tryNameStrategy,
		// tryPlaceholderStrategy,
		tryIdStrategy,		
		// tryClassStrategy
	];
	
	for (const strategy of strategies) {
		const result = strategy(element);      
		if (result) {        
			return result;
		}
	}
	return null;
}

/**
 * Generate a smart selector for an element
 * @param {Element} element - The element to generate a locator for
 * @param {string} childPath - The child path to generate a locator for
 * @returns {Object} The selector for the element
 */
export function generateSmartSelector(element, childPath = '') {
	// Terminate recursion
	if (!element || element.nodeType !== Node.ELEMENT_NODE) {
		return {
			strategy: 'css',
			selector: childPath
		};
	}
	
	// Try strategies 1-6 on current element
	const result = attemptStrategiesOnElement(element);
	const is_label = result?.strategy === 'label for' || result?.strategy === 'label by';

	console.log('result', result);
	if (result) { // found a strategy			
		// If we have a child path, concatenate parent strategy result with child CSS path            
		let extendedResult = result;        
		if (childPath) {
			const selector = is_label ? `${childPath}` : `${result?.selector} > ${childPath}`;
			extendedResult = {
				...extendedResult,
				selector: selector,
				strategy: `${extendedResult.strategy} (concatenated with child CSS path)`
			};
		}      		
		// check if the combined selector is unique and if not add the index to the strategy
		if (!is_label) { //label element is already checked for uniqueness
			try {
				const matchedElements = querySelectorAllByText(extendedResult?.selector); //query full selector for uniqueness checking   
				highlighterLogger.debug(`querySelectorAllByText(${extendedResult?.selector}) matched ${matchedElements.length} elements`);
				highlighterLogger.debug(matchedElements);
				if (matchedElements.length > 1) {
					const childElement = childPath ? robustQuerySelector(`:scope > ${childPath}`, element) : element;
					const index = matchedElements.findIndex(el => el === childElement);
					if (index === -1) {
						highlighterLogger.error('internal error: Element not found in matched elements', element, result?.selector, matchedElements);
						return null;
					}
					extendedResult = {
						...extendedResult,
						strategy: `${extendedResult.strategy} (by index)`,
						index,
					};        
				}
			} catch (error) {
				highlighterLogger.error(`internal error: while checking if selector ${result?.selector} is unique for element ${element.outerHTML}`, error);
				throw error;
			}			
		}
		return extendedResult;
	}
	
	// Get CSS selector for current element and build child path    
	let newChildPath;
	// didn't find strategy
	const elementSelector = getElementCssSelector(element);
	newChildPath = childPath ? `${elementSelector} > ${childPath}` : elementSelector;
	const numComponents = newChildPath.split(' > ').length;
	if (numComponents > 2) { //require at least 2 components for css strategy
		// check if the combined selector is unique
		const matchedElements = robustQuerySelector(newChildPath, document, true);
		if (matchedElements.length === 1) {
			return {
				strategy: 'css',				
				selector: newChildPath
			};
		}
	}

	// Recursively try parent element
	return generateSmartSelector(getParentNode(element), newChildPath);
}

export const findElementByInfo = (elementInfo, useSmartSelectors = false) => {
	highlighterLogger.debug('findElementByInfo:', elementInfo);
	if (useSmartSelectors) {
	  return findElementBySelectors(elementInfo.smart_iframe_selector, elementInfo.smart_selector, true);
	}
	return findElementBySelectors(elementInfo.iframe_selector, elementInfo.css_selector, false);
}

export const compareSelectors = (selector1, selector2, useSmartSelectors = false) => {
	if (useSmartSelectors) {
		return JSON.stringify(selector1) === JSON.stringify(selector2);
	}
	return selector1 === selector2;
}

export const findElementBySelectors = (iframeSelector, elementSelector, useSmartSelectors = false) => {
	let element;
	
	// first find the iframe that contains the element
	if (iframeSelector) {      
	  const frames = getAllDocumentElementsIncludingShadow('iframe', document);
	  
	  // Iterate over all frames and compare their CSS selectors
	  for (const frame of frames) {
		const selector = useSmartSelectors ? generateSmartSelector(frame) : generateCssPath(frame);
		if (compareSelectors(selector, iframeSelector, useSmartSelectors)) {
		  const frameDocument = frame.contentDocument || frame.contentWindow.document;
		  element = useSmartSelectors ? findElementBySmartSelector(elementSelector, frameDocument) : robustQuerySelector(elementSelector, frameDocument);		  
		  break;
		}        
	  }; 
	}
	else {		
		element = useSmartSelectors ? findElementBySmartSelector(elementSelector, document) : robustQuerySelector(elementSelector, document);		
	}
	  
	if (element) {
		highlighterLogger.debug('findElementBySelectors: found element ', element);
	}
	else {
	  highlighterLogger.warn('findElementBySelectors: failed to find element with CSS selector:', JSON.stringify(elementSelector));
	}
  
	return element;
  };
  
export function findElementBySmartSelector(smartSelector, root = document) {	
	highlighterLogger.debug(`findElementBySmartSelector: called with smartSelector=${JSON.stringify(smartSelector)}`);
	let combinedSelector;
	const mainStrategy = smartSelector.strategy.split(' ')[0]; //extract the main strategy from the full strategy string
	switch(mainStrategy) {
        case 'role': 
        case 'placeholder':
        case 'name':
        case 'data-testid':
        case 'id':
        case 'css':
        case 'class':
		  highlighterLogger.debug(`findElementBySmartSelector: strategy='${smartSelector.strategy}', selector=${smartSelector?.selector}, index=${smartSelector?.index}`);
          if (typeof smartSelector?.index === 'number') {
            return querySelectorAllByText(smartSelector.selector, root)[smartSelector.index];
          }
          else {
            return querySelectorAllByText(smartSelector.selector, root)[0];
          }		  
        case 'label':
			if (smartSelector.strategy === 'label for') {
				highlighterLogger.debug(`findElementBySmartSelector: strategy='label for', label_selector=${smartSelector.label_selector}`);
				const label_for = querySelectorAllByText(smartSelector.label_selector, root)[0];
				combinedSelector = smartSelector?.selector ? `#${label_for.getAttribute('for')} > ${smartSelector.selector}` : `#${label_for.getAttribute('for')}`;
				return robustQuerySelector(combinedSelector, root);
			}
			else if (smartSelector.strategy === 'label by') {
				highlighterLogger.debug(`findElementBySmartSelector: strategy='label by', label_selector=${smartSelector.label_selector}`);
				const label_by = querySelectorAllByText(smartSelector.label_selector, root)[0];
				combinedSelector = smartSelector?.selector ? `[aria-labelledby="${label_by.getAttribute('id')}"] > ${smartSelector.selector}` : `[aria-labelledby="${label_by.getAttribute('id')}"]`;
				return robustQuerySelector(combinedSelector, root);
			}
			else {
				highlighterLogger.error(`findElementBySmartSelector: unsupported label strategy: ${smartSelector.strategy}`);
				return null;
			}        
        default:
          highlighterLogger.error(`findElementBySmartSelector: Unsupported smart selector strategy: ${smartSelector.strategy}`);
          return null;
        }
}

export function getElementInfo(element, index) {    			
	const iframe = getContainingIFrame(element);  
	const iframe_selector = iframe ? generateCssPath(iframe) : "";	
	const smart_iframe_selector = iframe ? generateSmartSelector(iframe) : null;
	
	// Return element info with pre-calculated values
	return {
		index: index ?? -1,
		tag: element.tagName.toLowerCase(),
		type: element.type || '',
		text: getElementText(element) || getElementPlaceholder(element),
		html: cleanHTML(element.outerHTML),
		xpath: generateXPath(element),
		css_selector: generateCssPath(element),
		bounding_box: element.getBoundingClientRect(),		
		iframe_selector: iframe_selector,		
		smart_selector: generateSmartSelector(element),
		smart_iframe_selector: smart_iframe_selector,
		element: element,
		depth: getElementDepth(element)
	};
}